const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/bmp']);
const IMAGE_UPLOAD_COMPRESS_MIN_BYTES = 256 * 1024;
const IMAGE_UPLOAD_MAX_EDGE = 2400;
const IMAGE_UPLOAD_JPEG_QUALITY = 0.86;

export function shouldCompressUploadImage(file: Pick<File, 'type' | 'size'>): boolean {
  return COMPRESSIBLE_IMAGE_TYPES.has(file.type.toLowerCase()) && file.size >= IMAGE_UPLOAD_COMPRESS_MIN_BYTES;
}

export function compressedImageName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'image.jpg';
  const slash = trimmed.lastIndexOf('/');
  const dir = slash >= 0 ? trimmed.slice(0, slash + 1) : '';
  const base = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return `${dir}${stem || 'image'}.jpg`;
}

export async function compressImageForUpload(file: File): Promise<File> {
  if (!shouldCompressUploadImage(file)) return file;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const scale = Math.min(1, IMAGE_UPLOAD_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToJpegBlob(canvas);
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], compressedImageName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', IMAGE_UPLOAD_JPEG_QUALITY);
  });
}
